import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Chip, TextField,
  Button, IconButton, Tooltip, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  Autocomplete, ToggleButtonGroup, ToggleButton, InputAdornment,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Balance as BalanceIcon, Search as SearchIcon, FileDownload as ExportIcon,
  Print as PrintIcon, Description as WordIcon,
  TrendingDown as ArrearsIcon, Savings as CreditIcon, HourglassTop as PendingIcon,
  Group as DebtorIcon, Refresh as RefreshIcon,
  Payments as IncomeIcon, Class as ClassIcon,
  InfoOutlined as InfoIcon, Insights as AnalysisIcon,
  TableChart as TableIcon, CalendarMonth as DayIcon,
  Restaurant as ServiceIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import ServiceListTab from './ServiceListTab';
import API from '../../BaseURL';
import swal from '../../utils/swal';

const FONT_ACCT = `'IBM Plex Sans', 'Inter', sans-serif`;
const TNUM = { fontVariantNumeric: 'tabular-nums' };

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDateTime = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

// A column heading that can explain itself. The figures on this page are
// easy to misread — "owes" and "awaiting approval" pull in opposite
// directions — and a heading nobody can interpret is worse than no
// column at all.
function HeadCell({ label, hint }) {
  return (
    <Tooltip title={hint} arrow placement="top">
      <Box component="span" sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.4, cursor: 'help',
        borderBottom: '1px dotted', borderColor: 'text.disabled',
      }}>
        {label}
        <InfoIcon sx={{ fontSize: 13, opacity: 0.55 }} />
      </Box>
    </Tooltip>
  );
}

function StatTile({ icon, label, value, color = 'default', sub }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ py: 1.4, '&:last-child': { pb: 1.4 } }}>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Box sx={{
            p: 0.9, borderRadius: 1.5,
            bgcolor: (t) => color === 'default'
              ? alpha(t.palette.text.secondary, 0.1)
              : alpha(t.palette[color].main, 0.12),
            color: (t) => color === 'default' ? t.palette.text.secondary : t.palette[color].main,
            display: 'flex',
          }}>
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {label}
            </Typography>
            <Typography variant="h6" fontWeight={800}
              sx={{ ...TNUM, lineHeight: 1.1,
                color: (t) => color === 'default' ? t.palette.text.primary : t.palette[color].main }}>
              {value}
            </Typography>
            {sub && (
              <Typography variant="caption" color="text.secondary">{sub}</Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Day Analysis ───────────────────────────────────────────────
//
// The Balances table answers "where does everyone stand?" — a running
// total that says nothing about today. This answers the question the
// bursar actually asks each afternoon: who paid, who did not, how much
// came in, and which classes are drifting into arrears.
//
// Every figure comes from one grouped query over daily_attendance, so
// the class rows, the income-type rows and the totals are folds of the
// same data and cannot disagree with each other.
function DayAnalysis({ classes, incomeTypes }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate]       = useState('');
  const [cls, setCls]         = useState('');
  const [inc, setInc]         = useState('');
  const [view, setView]       = useState('class');   // class | type | matrix
  const [listKind, setListKind] = useState('paid');  // what the printed list shows

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (date) params.date = date;
      if (cls)  params.class_name = cls;
      if (inc)  params.income_type = inc;
      const r = await API.get('/daily-subscriptions/day-analysis', { params });
      setData(r.data);
      if (!date && r.data?.date) setDate(r.data.date);
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not load the day analysis'));
      setData(null);
    } finally { setLoading(false); }
  };
  // `load` is redefined every render, so listing it would re-fetch in a
  // loop. The filters above are the real triggers.
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, cls, inc]);

  const T = data?.totals || {};
  const pct = (a, b) => (Number(b) > 0 ? Math.round((Number(a) / Number(b)) * 100) : 0);

  // ── The paper list ─────────────────────────────────────────────
  //
  // The table answers "17 paid in Class 2". The kitchen needs the
  // seventeen NAMES, on paper, before lunch — the list somebody was
  // otherwise copying out by hand from a screen of totals.
  //
  // Laid out the way the school already writes it: classes side by side,
  // names numbered within each, and a summary block counting every class
  // and the total. CSS columns flow the classes into newspaper columns so
  // a whole school fits on one or two sheets, and `break-inside: avoid`
  // stops a class being split across a column break.
  const LIST_KINDS = {
    paid:   { label: 'PAID',        keep: (r) => r.status === 'full' || r.status === 'partial' },
    unpaid: { label: 'DID NOT PAY', keep: (r) => r.status === 'none' },
    absent: { label: 'ABSENT',      keep: (r) => r.status === 'absent' },
    all:    { label: 'ALL MARKED',  keep: () => true },
  };

  const esc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const printList = () => {
    const kind = LIST_KINDS[listKind] || LIST_KINDS.paid;
    const all  = (data?.roster || []).filter(kind.keep);
    if (!all.length) {
      swal.info(`Nobody on this day matches "${kind.label.toLowerCase()}".`, 'Nothing to print');
      return;
    }

    // Group by class, keeping the server's name ordering.
    const byCls = new Map();
    for (const r of all) {
      const c = r.class_name || '—';
      if (!byCls.has(c)) byCls.set(c, []);
      byCls.get(c).push(r);
    }
    const classNames = [...byCls.keys()].sort();

    // dd-mm-yyyy, the way the school writes a date on a printed list.
    const d = String(data?.date || '');
    const dmy = /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d.slice(8,10)}-${d.slice(5,7)}-${d.slice(0,4)}` : d;
    const title = `${(inc || 'DAILY COLLECTION').toUpperCase().replace(/_/g, ' ')} LIST`;
    const sub   = kind.label === 'ALL MARKED' ? '' : ` — ${kind.label}`;

    const blocks = classNames.map(c => `
      <div class="cls">
        <div class="clsname">${esc(c)}</div>
        <ol>${byCls.get(c).map(r => `<li>${esc(r.student_name)}</li>`).join('')}</ol>
      </div>`).join('');

    const summary = `
      <table class="sum">
        <thead><tr><th>CLASS</th><th class="r">COUNT</th></tr></thead>
        <tbody>
          ${classNames.map(c => `<tr><td>${esc(c)}</td><td class="r">${byCls.get(c).length}</td></tr>`).join('')}
          <tr class="tot"><td>Total</td><td class="r">${all.length}</td></tr>
        </tbody>
      </table>`;

    const html = `<!doctype html><html><head><meta charset="utf-8">
      <title>${esc(title)} ${esc(dmy)}</title>
      <style>
        @page { size: A4 portrait; margin: 12mm; }
        body { font-family: Arial, Helvetica, sans-serif; color:#000; margin:0; }
        .hdr { background:#e11d1d; color:#fff; text-align:center; font-weight:800;
               padding:6px 10px; font-size:15px; letter-spacing:.3px; }
        .meta { text-align:center; font-size:11px; margin:4px 0 10px; color:#333; }
        .wrap { column-count: 4; column-gap: 10px; }
        @media print { .wrap { column-count: 4; } }
        .cls { break-inside: avoid; page-break-inside: avoid; margin:0 0 10px; }
        .clsname { font-weight:800; font-size:12px; border-bottom:1.5px solid #000;
                   padding-bottom:2px; margin-bottom:3px; }
        ol { margin:0; padding-left:20px; }
        li { font-size:11px; line-height:1.45; }
        .sum { border-collapse:collapse; margin-top:14px; width:220px; }
        .sum th, .sum td { border:1px solid #333; padding:3px 7px; font-size:11px; }
        .sum th { background:#f0f0f0; }
        .r { text-align:right; }
        .tot td { font-weight:800; background:#f7f7f7; }
        .foot { margin-top:12px; font-size:10px; color:#555; }
      </style></head><body>
      <div class="hdr">${esc(title)}${esc(sub)} (${esc(dmy)})</div>
      <div class="meta">
        ${esc(cls || 'All classes')} &middot; ${all.length} learner${all.length === 1 ? '' : 's'}
      </div>
      <div class="wrap">${blocks}</div>
      ${summary}
      <div class="foot">Printed ${new Date().toLocaleString()}</div>
      <script>window.onload = function(){ window.print(); }${'<'}/script>
      </body></html>`;

    const w = window.open('', '_blank');
    if (!w) {
      swal.warning('Your browser blocked the print window. Allow pop-ups for this site and try again.');
      return;
    }
    w.document.open(); w.document.write(html); w.document.close();
  };

  // One row shape for all three views, so the table below stays simple.
  const rows = !data ? []
    : view === 'class'  ? (data.by_class || []).map(r => ({ ...r, k: r.class_name, a: r.class_name, b: '' }))
    : view === 'type'   ? (data.by_income_type || []).map(r => ({ ...r, k: r.income_type, a: r.income_type, b: '' }))
    : (data.matrix || []).map(r => ({ ...r, k: `${r.class_name}|${r.income_type}`, a: r.class_name, b: r.income_type }));

  // Fixed-height so a tile with no sub-line does not sit shorter than
  // its neighbours, and a left accent rule so the eye can group them.
  const Tile = ({ label, value, sub, tone }) => (
    <Card variant="outlined" sx={{
      borderRadius: 2, height: '100%',
      borderLeft: '3px solid',
      borderLeftColor: tone ? `${tone}.main` : 'divider',
    }}>
      <CardContent sx={{ py: 1.2, px: 1.6, '&:last-child': { pb: 1.2 } }}>
        <Typography noWrap sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
          textTransform: 'uppercase', color: 'text.secondary' }}>
          {label}
        </Typography>
        <Typography noWrap sx={{ ...TNUM, fontSize: 19, fontWeight: 800, lineHeight: 1.25,
          color: tone ? `${tone}.main` : 'text.primary' }}>
          {value}
        </Typography>
        <Typography sx={{
          fontSize: 10.5, color: 'text.secondary', lineHeight: 1.3,
          minHeight: 27,   // reserve two lines so every tile is the same height
        }}>
          {sub || ''}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Controls */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
        <CardContent sx={{ py: 1.6, '&:last-child': { pb: 1.6 },
          display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* shrink is forced on every one of these: a native select
              always renders a value, so a floating label would sit on
              top of it — which is exactly what "All classes" and
              "All types" were doing. */}
          <TextField
            select SelectProps={{ native: true }} size="small" label="Day"
            InputLabelProps={{ shrink: true }}
            value={date} onChange={(e) => setDate(e.target.value)}
            sx={{ minWidth: 190 }}
            InputProps={{ startAdornment: (
              <InputAdornment position="start"><DayIcon fontSize="small" /></InputAdornment>) }}>
            {(data?.recent_dates || []).map(d => <option key={d} value={d}>{d}</option>)}
            {!(data?.recent_dates || []).length && <option value="">No collection days yet</option>}
          </TextField>
          <TextField select SelectProps={{ native: true }} size="small" label="Class"
            InputLabelProps={{ shrink: true }}
            value={cls} onChange={(e) => setCls(e.target.value)} sx={{ minWidth: 190 }}>
            <option value="">All classes</option>
            {classes.map(c => <option key={c} value={c}>{c}</option>)}
          </TextField>
          <TextField select SelectProps={{ native: true }} size="small" label="Income type"
            InputLabelProps={{ shrink: true }}
            value={inc} onChange={(e) => setInc(e.target.value)} sx={{ minWidth: 190 }}>
            <option value="">All types</option>
            {incomeTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </TextField>
          <Box sx={{ flex: 1 }} />
          <ToggleButtonGroup size="small" exclusive value={view}
            onChange={(_, v) => v && setView(v)}>
            <ToggleButton value="class">By class</ToggleButton>
            <ToggleButton value="type">By income type</ToggleButton>
            <ToggleButton value="matrix">Both</ToggleButton>
          </ToggleButtonGroup>
          <TextField select size="small" label="Print" value={listKind}
            onChange={(e) => setListKind(e.target.value)}
            SelectProps={{ native: true }} sx={{ minWidth: 130 }}>
            <option value="paid">Who paid</option>
            <option value="unpaid">Who did not pay</option>
            <option value="absent">Who was absent</option>
            <option value="all">Everyone marked</option>
          </TextField>
          <Tooltip title="Print the class-by-class name list">
            <span>
              <Button size="small" variant="outlined" startIcon={<PrintIcon />}
                disabled={!data || !(data.roster || []).length}
                onClick={printList}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                Print list
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton>
          </Tooltip>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>
      ) : !data || !Number(T.marked) ? (
        <Card variant="outlined" sx={{ borderRadius: 2, py: 7, textAlign: 'center' }}>
          <AnalysisIcon sx={{ fontSize: 42, color: 'text.disabled' }} />
          <Typography sx={{ fontWeight: 700, mt: 1 }}>Nothing was marked on {date || 'this day'}</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Pick another day, or open a collection session on the Daily Collection page.
          </Typography>
        </Card>
      ) : (
        <>
          {/* Which sessions ran that day */}
          {(data.sessions || []).length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', pt: 0.6 }}>
                Sessions on {data.date}:
              </Typography>
              {data.sessions.map(s => (
                <Chip key={s.income_type} size="small"
                  label={`${s.income_type} · ${s.status}`}
                  color={s.status === 'closed' ? 'default' : 'primary'}
                  variant={s.status === 'closed' ? 'outlined' : 'filled'} />
              ))}
            </Stack>
          )}

          {/* A grid, not a wrapping flex row. Eight tiles in a flex-wrap
              left two stranded on a second row stretched to half the
              screen each; an explicit column count always divides
              evenly — 4 across on a laptop, all 8 on a wide monitor. */}
          <Box sx={{
            display: 'grid', gap: 1.2, mb: 2,
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(4, 1fr)',
              xl: 'repeat(8, 1fr)',
            },
          }}>
            <Tile label="Came in" value={`GHS ${fmtMoney(T.collected)}`} tone="success"
              sub={`of GHS ${fmtMoney(T.expected)} chargeable`} />
            <Tile label="Paid in full" value={T.full_paid}
              sub={`${pct(T.full_paid, T.marked)}% of ${T.marked} marked`} />
            <Tile label="Part paid" value={T.part_paid}
              tone={Number(T.part_paid) ? 'warning' : undefined}
              sub={Number(T.part_paid) ? 'paid less than the daily rate' : 'none'} />
            <Tile label="Did not pay" value={T.not_paid}
              tone={Number(T.not_paid) ? 'error' : undefined}
              sub={`GHS ${fmtMoney(T.arrears_added)} added to arrears`} />
            <Tile label="Absent" value={T.absent} sub={Number(T.absent) ? 'not charged' : 'none'} />
            <Tile label="Paid ahead" value={T.paid_ahead} tone="info"
              sub={`GHS ${fmtMoney(T.advance_added)} banked`} />
            <Tile label="Used credit" value={`GHS ${fmtMoney(T.from_credit)}`}
              sub="settled from money paid earlier" />
            <Tile label="Learners" value={T.learners} sub={`${T.marked} subscription rows`} />
          </Box>

          {/* On a wide screen the main table left the right-hand third
              empty. The income-type split moves in there, so both cuts
              of the same day are on screen at once instead of one being
              a toggle away. */}
          <Box sx={{
            display: 'grid', gap: 1.5, alignItems: 'start',
            gridTemplateColumns: {
              xs: '1fr',
              lg: view === 'class' ? 'minmax(0, 1fr) 340px' : '1fr',
            },
          }}>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small" sx={{
              '& th': { fontWeight: 800, fontSize: 11, textTransform: 'uppercase',
                letterSpacing: 0.4, bgcolor: (t) => alpha(t.palette.primary.main, 0.06) },
              '& td': { fontSize: 12.5 },
            }}>
              <TableHead>
                <TableRow>
                  <TableCell>{view === 'type' ? 'Income type' : 'Class'}</TableCell>
                  {view === 'matrix' && <TableCell>Income type</TableCell>}
                  <TableCell align="right">Marked</TableCell>
                  <TableCell align="right">
                    <HeadCell label="Paid" hint="Marked as having paid the full daily amount." />
                  </TableCell>
                  <TableCell align="right">
                    <HeadCell label="Part" hint="Paid something, but less than the daily amount." />
                  </TableCell>
                  <TableCell align="right">
                    <HeadCell label="Unpaid" hint="Owed the day and paid nothing — it went to arrears." />
                  </TableCell>
                  <TableCell align="right">Absent</TableCell>
                  <TableCell align="right">
                    <HeadCell label="Came in" hint="Cash actually taken on the day." />
                  </TableCell>
                  <TableCell align="right">
                    <HeadCell label="To arrears" hint="Charged but not paid — added to what the family owes." />
                  </TableCell>
                  <TableCell align="right">
                    <HeadCell label="Paid ahead" hint="Overpayment kept as credit for future days." />
                  </TableCell>
                  <TableCell align="right" sx={{ width: 120 }}>
                    <HeadCell label="Collected" hint="Share of the day's charge that was actually paid." />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => {
                  const rate = pct(r.collected, r.expected);
                  return (
                    <TableRow key={r.k} hover>
                      <TableCell sx={{ fontWeight: 700 }}>{r.a}</TableCell>
                      {view === 'matrix' && (
                        <TableCell>
                          <Chip size="small" label={r.b} variant="outlined"
                            sx={{ height: 20, fontSize: 10.5 }} />
                        </TableCell>
                      )}
                      <TableCell align="right" sx={TNUM}>{r.marked}</TableCell>
                      <TableCell align="right" sx={{ ...TNUM, color: 'success.dark', fontWeight: 700 }}>
                        {r.full_paid || '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ ...TNUM, color: r.part_paid ? 'warning.dark' : 'text.disabled' }}>
                        {r.part_paid || '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ ...TNUM, color: r.not_paid ? 'error.main' : 'text.disabled',
                        fontWeight: r.not_paid ? 700 : 400 }}>
                        {r.not_paid || '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ ...TNUM, color: 'text.secondary' }}>
                        {r.absent || '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ ...TNUM, fontWeight: 700 }}>
                        {fmtMoney(r.collected)}
                      </TableCell>
                      <TableCell align="right" sx={{ ...TNUM, color: Number(r.arrears_added) > 0 ? 'error.main' : 'text.disabled' }}>
                        {Number(r.arrears_added) > 0 ? fmtMoney(r.arrears_added) : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ ...TNUM, color: Number(r.advance_added) > 0 ? 'info.main' : 'text.disabled' }}>
                        {Number(r.advance_added) > 0 ? fmtMoney(r.advance_added) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.8} alignItems="center" justifyContent="flex-end">
                          <Box sx={{ width: 46, height: 6, borderRadius: 3, bgcolor: 'grey.200',
                            overflow: 'hidden' }}>
                            <Box sx={{ width: `${rate}%`, height: '100%',
                              bgcolor: rate >= 80 ? 'success.main' : rate >= 40 ? 'warning.main' : 'error.main' }} />
                          </Box>
                          <Typography sx={{ ...TNUM, fontSize: 11.5, fontWeight: 700, minWidth: 30 }}>
                            {rate}%
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow sx={{ '& td': { fontWeight: 800, borderTop: '2px solid',
                  borderColor: 'divider', bgcolor: (t) => alpha(t.palette.primary.main, 0.03) } }}>
                  <TableCell>Total</TableCell>
                  {view === 'matrix' && <TableCell />}
                  <TableCell align="right" sx={TNUM}>{T.marked}</TableCell>
                  <TableCell align="right" sx={TNUM}>{T.full_paid}</TableCell>
                  <TableCell align="right" sx={TNUM}>{T.part_paid}</TableCell>
                  <TableCell align="right" sx={TNUM}>{T.not_paid}</TableCell>
                  <TableCell align="right" sx={TNUM}>{T.absent}</TableCell>
                  <TableCell align="right" sx={TNUM}>{fmtMoney(T.collected)}</TableCell>
                  <TableCell align="right" sx={TNUM}>{fmtMoney(T.arrears_added)}</TableCell>
                  <TableCell align="right" sx={TNUM}>{fmtMoney(T.advance_added)}</TableCell>
                  <TableCell align="right" sx={TNUM}>{pct(T.collected, T.expected)}%</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* The same day, cut by income type. Only beside the class
              view — next to the income-type table itself it would just
              be the same numbers twice. */}
          {view === 'class' && (
            <Card variant="outlined" sx={{ borderRadius: 2, display: { xs: 'none', lg: 'block' } }}>
              <Box sx={{ px: 2, py: 1.2, borderBottom: 1, borderColor: 'divider',
                bgcolor: (t) => alpha(t.palette.primary.main, 0.04) }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.4,
                  textTransform: 'uppercase', color: 'text.secondary' }}>
                  What they paid for
                </Typography>
              </Box>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                {(data.by_income_type || []).map((t, i) => {
                  const rate = pct(t.collected, t.expected);
                  return (
                    <Box key={t.income_type} sx={{ mb: i === (data.by_income_type.length - 1) ? 0 : 1.8 }}>
                      <Stack direction="row" alignItems="baseline" sx={{ mb: 0.4 }}>
                        <Typography sx={{ flex: 1, fontSize: 12.5, fontWeight: 800 }}>
                          {t.income_type}
                        </Typography>
                        <Typography sx={{ ...TNUM, fontSize: 13, fontWeight: 800 }}>
                          GHS {fmtMoney(t.collected)}
                        </Typography>
                      </Stack>
                      <Box sx={{ height: 7, borderRadius: 4, bgcolor: 'grey.200', overflow: 'hidden', mb: 0.5 }}>
                        <Box sx={{ width: `${rate}%`, height: '100%',
                          bgcolor: rate >= 80 ? 'success.main' : rate >= 40 ? 'warning.main' : 'error.main' }} />
                      </Box>
                      <Stack direction="row" spacing={1.2} sx={{ fontSize: 11, color: 'text.secondary' }}>
                        <Box component="span" sx={{ color: 'success.dark', fontWeight: 700 }}>
                          {t.full_paid} paid
                        </Box>
                        {Number(t.part_paid) > 0 && (
                          <Box component="span" sx={{ color: 'warning.dark', fontWeight: 700 }}>
                            {t.part_paid} part
                          </Box>
                        )}
                        <Box component="span" sx={{ color: Number(t.not_paid) ? 'error.main' : 'text.disabled',
                          fontWeight: Number(t.not_paid) ? 700 : 400 }}>
                          {t.not_paid} unpaid
                        </Box>
                        <Box sx={{ flex: 1 }} />
                        <Box component="span">{rate}%</Box>
                      </Stack>
                      {Number(t.arrears_added) > 0 && (
                        <Typography sx={{ fontSize: 10.5, color: 'error.main', mt: 0.3 }}>
                          GHS {fmtMoney(t.arrears_added)} went to arrears
                        </Typography>
                      )}
                    </Box>
                  );
                })}
                {!(data.by_income_type || []).length && (
                  <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>Nothing recorded.</Typography>
                )}
              </CardContent>
            </Card>
          )}
          </Box>
        </>
      )}
    </Box>
  );
}

export default function SubscriptionBalancesPage() {
  const [classes, setClasses]         = useState([]);
  const [incomeTypes, setIncomeTypes] = useState([]);

  // 'service'  = who to serve today — the default, because it is the
  //              only one of the three that is needed BEFORE the day
  //              happens. A cook opening this page at seven in the
  //              morning wants the list, not a term's arrears.
  // 'balances' = the standing position.
  // 'analysis' = one day's activity, after the fact.
  const [section, setSection]         = useState('service');

  const [filtIncome, setFiltIncome]   = useState(null);
  const [filtClass, setFiltClass]     = useState('');
  const [filtQuery, setFiltQuery]     = useState('');
  const [only, setOnly]               = useState('');     // '' | 'owing' | 'credit'

  const [rows, setRows]               = useState([]);
  const [totals, setTotals]           = useState({
    arrears: 0, credit: 0, pending: 0, debtors: 0, creditors: 0, count: 0,
  });
  const [loading, setLoading]         = useState(true);

  const loadRefs = async () => {
    try {
      const [c, it] = await Promise.all([
        API.get('/classes'),
        API.get('/collections/income-types'),
      ]);
      setClasses(c.data || []);
      setIncomeTypes(it.data || []);
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not load reference data'));
    }
  };

  const loadBalances = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtIncome) params.income_type = filtIncome.type;
      if (filtClass)  params.class_name  = filtClass;
      if (filtQuery)  params.q           = filtQuery;
      if (only)       params.only        = only;
      const r = await API.get('/daily-subscriptions/balances', { params });
      setRows(r.data?.rows || []);
      setTotals(r.data?.totals || { arrears: 0, credit: 0, pending: 0, debtors: 0, creditors: 0, count: 0 });
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not load balances'));
    } finally { setLoading(false); }
  };

  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { loadBalances(); /* eslint-disable-next-line */ },
    [filtIncome, filtClass, filtQuery, only]);

  const classOptions = useMemo(() => classes.map(c => c.class).filter(Boolean), [classes]);
  const incomeOptions = useMemo(
    () => incomeTypes.map(t => ({ trans: t.trans, type: t.type, cat: t.cat })),
    [incomeTypes]
  );

  // CSV export — same shape as the table.
  const exportCsv = () => {
    if (!rows.length) return swal.error('Nothing to export', 'No rows');
    const head = [
      'Index', 'Student', 'Class', 'Income Type', 'Daily', 'Arrears',
      'Credit', 'Pending Payment', 'Last Pending At',
    ];
    const escape = (v) => {
      if (v == null) return '';
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const data = rows.map(r => [
      r.student_id, r.student_name, r.class_name || '', r.income_type,
      Number(r.daily_amount) || 0,
      Number(r.arrears) || 0,
      Number(r.credit)  || 0,
      Number(r.pending_payment) || 0,
      r.last_pending_at || '',
    ]);
    const csv = [head, ...data].map(row => row.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `subscription-balances-${stamp}.csv`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // Build the report HTML once and reuse it for Print and Word export.
  // Inline styles only — print windows don't see emotion classes.
  const buildReportHTML = () => {
    const stamp = new Date().toLocaleString();
    const filterBits = [];
    if (filtIncome) filterBits.push(`Income: ${filtIncome.type}`);
    if (filtClass)  filterBits.push(`Class: ${filtClass}`);
    if (filtQuery)  filterBits.push(`Search: "${filtQuery}"`);
    if (only)       filterBits.push(only === 'owing' ? 'Owing only' : 'Credit only');
    const filterLine = filterBits.length ? filterBits.join(' · ') : 'All students, all income types';
    const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) =>
      ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
    const body = rows.map((r, i) => `
      <tr>
        <td style="text-align:right">${i + 1}</td>
        <td>${esc(r.student_name || '')}</td>
        <td>${esc(r.student_id)}</td>
        <td>${esc(r.class_name || '-')}</td>
        <td>${esc(r.income_type)}</td>
        <td style="text-align:right">${fmtMoney(r.daily_amount)}</td>
        <td style="text-align:right; color:${Number(r.arrears) > 0 ? '#b91c1c' : '#6b7280'}; font-weight:${Number(r.arrears) > 0 ? 700 : 500}">${fmtMoney(r.arrears)}</td>
        <td style="text-align:right; color:${Number(r.credit) > 0 ? '#15803d' : '#6b7280'}; font-weight:${Number(r.credit) > 0 ? 700 : 500}">${fmtMoney(r.credit)}</td>
        <td style="text-align:right; color:${Number(r.pending_payment) > 0 ? '#b45309' : '#9ca3af'}">${Number(r.pending_payment) > 0 ? fmtMoney(r.pending_payment) : '-'}</td>
      </tr>`).join('');
    return `<!DOCTYPE html><html><head><meta charset="utf-8" />
      <title>Daily Collection Balances</title>
      <style>
        body { font-family: 'Times New Roman', Georgia, serif; color: #111; padding: 28px; }
        h1   { margin: 0 0 4px 0; font-size: 20px; }
        .sub { color: #475569; font-size: 11px; margin-bottom: 14px; }
        .meta { display: flex; gap: 16px; flex-wrap: wrap; font-size: 11px;
                color: #475569; margin-bottom: 8px; }
        .meta b { color: #111; font-weight: 700; }
        .totals { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px;
                  margin: 8px 0 14px; font-size: 11px; }
        .totals .box { border: 1px solid #cbd5e1; border-radius: 6px; padding: 8px 10px; }
        .totals .label { color:#64748b; text-transform: uppercase;
                         letter-spacing: 0.5px; font-weight: 800; font-size: 10px; }
        .totals .value { font-weight: 800; font-size: 14px; margin-top: 2px;
                         font-variant-numeric: tabular-nums; }
        table { width: 100%; border-collapse: collapse; font-size: 10.5px;
                font-variant-numeric: tabular-nums; }
        th    { background: #f1f5f9; border: 1px solid #cbd5e1; padding: 5px 6px;
                text-align: left; text-transform: uppercase; letter-spacing: 0.4px;
                font-size: 9.5px; font-weight: 800; color: #475569; }
        td    { border: 1px solid #e2e8f0; padding: 4px 6px; }
        tfoot td { border-top: 2px solid #111; font-weight: 800; }
        @media print { body { padding: 12mm; } }
      </style>
      </head><body>
        <h1>Daily Collection Balances</h1>
        <div class="sub">Generated ${stamp}</div>
        <div class="meta"><b>Filters:</b> ${esc(filterLine)} <b>Rows:</b> ${rows.length}</div>
        <div class="totals">
          <div class="box"><div class="label">Total Arrears</div>
            <div class="value" style="color:#b91c1c">GHS ${fmtMoney(totals.arrears)}</div>
            <div style="color:#64748b">${totals.debtors} debtor${totals.debtors === 1 ? '' : 's'}</div></div>
          <div class="box"><div class="label">Total Credit</div>
            <div class="value" style="color:#15803d">GHS ${fmtMoney(totals.credit)}</div>
            <div style="color:#64748b">${totals.creditors} ahead of bill</div></div>
          <div class="box"><div class="label">Pending Approval</div>
            <div class="value" style="color:#b45309">GHS ${fmtMoney(totals.pending)}</div>
            <div style="color:#64748b">queued in collector approvals</div></div>
          <div class="box"><div class="label">Subscriptions</div>
            <div class="value">${totals.count}</div>
            <div style="color:#64748b">${filtIncome ? esc(filtIncome.type) : 'all income types'}</div></div>
        </div>
        <table>
          <thead><tr>
            <th style="width:30px; text-align:right">#</th>
            <th>Student</th><th>Index</th><th>Class</th><th>Income Type</th>
            <th style="text-align:right">Daily</th>
            <th style="text-align:right">Arrears</th>
            <th style="text-align:right">Credit</th>
            <th style="text-align:right">Pending</th>
          </tr></thead>
          <tbody>${body}</tbody>
        </table>
      </body></html>`;
  };

  const printReport = () => {
    if (!rows.length) return swal.error('Nothing to print', 'No rows');
    const w = window.open('', '_blank', 'width=1100,height=900');
    if (!w) return swal.error('Pop-up was blocked. Allow pop-ups and try again.', 'Print blocked');
    w.document.write(buildReportHTML());
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 350);
  };

  const exportWord = () => {
    if (!rows.length) return swal.error('Nothing to export', 'No rows');
    const blob = new Blob([buildReportHTML()], { type: 'application/msword' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `daily-collection-balances-${stamp}.doc`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    // Wider than the app's usual 1320. This page is a dense grid — nine
    // columns of balances, eleven of analysis — and at 1320 on a normal
    // monitor it left two empty margins while the student names wrapped
    // onto three lines inside a cramped column. The space was there; it
    // just was not being spent on anything.
    <Box sx={{ maxWidth: 1760, mx: 'auto', px: { xs: 0, xl: 1 }, fontFamily: FONT_ACCT }}>
      <ModuleHeader
        icon={<BalanceIcon />}
        title="Daily Collection Balances"
        subtitle="Who owes, who has credit, what's queued for approval - across all daily income types"
      />

      {/* ── The two things this page is for ──────────────────────
          Standing balances, and what happened on one day. They answer
          different questions and were never going to fit in one table,
          so they get a rail rather than being crammed together. */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Card variant="outlined" sx={{
          borderRadius: 2, width: { xs: 58, md: 208 }, flexShrink: 0,
          position: 'sticky', top: 12, overflow: 'hidden',
        }}>
          {[
            // First, because it is the one somebody needs BEFORE the
            // day happens rather than after it: the kitchen cannot wait
            // for a balance sheet to know who to serve.
            { key: 'service', label: 'Service List', icon: <ServiceIcon fontSize="small" />,
              blurb: 'Who to serve today' },
            { key: 'balances', label: 'Balances', icon: <TableIcon fontSize="small" />,
              blurb: 'Who owes, who is ahead' },
            { key: 'analysis', label: 'Day Analysis', icon: <AnalysisIcon fontSize="small" />,
              blurb: 'What happened today' },
          ].map((s, i) => {
            const on = section === s.key;
            return (
              <Box key={s.key} onClick={() => setSection(s.key)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.2, cursor: 'pointer',
                  px: { xs: 1.2, md: 1.8 }, py: 1.5,
                  borderTop: i === 0 ? 'none' : '1px solid', borderColor: 'divider',
                  borderLeft: '3px solid',
                  borderLeftColor: on ? 'primary.main' : 'transparent',
                  bgcolor: (t) => on ? alpha(t.palette.primary.main, 0.08) : 'transparent',
                  color: on ? 'primary.main' : 'text.primary',
                  transition: 'background-color .15s',
                  '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, on ? 0.08 : 0.04) },
                }}>
                {s.icon}
                <Box sx={{ display: { xs: 'none', md: 'block' }, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, lineHeight: 1.2 }}>
                    {s.label}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>
                    {s.blurb}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Card>

        <Box sx={{ flex: 1, minWidth: 0 }}>
        {section === 'service' ? (
          <ServiceListTab
            classes={classes.map(c => c.class_name || c.class || c).filter(Boolean)}
            incomeTypes={incomeTypes.map(t => t.type || t).filter(Boolean)}
          />
        ) : section === 'analysis' ? (
          <DayAnalysis
            classes={classes.map(c => c.class_name || c.class || c)}
            incomeTypes={incomeTypes.map(t => t.type || t)}
          />
        ) : (
        <>
      {/* Summary tiles */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
        gap: 1.2, mb: 2,
      }}>
        <StatTile color="error" icon={<ArrearsIcon fontSize="small" />}
          label="Total Arrears" value={`GHS ${fmtMoney(totals.arrears)}`}
          sub={`${totals.debtors} debtor${totals.debtors === 1 ? '' : 's'}`} />
        <StatTile color="success" icon={<CreditIcon fontSize="small" />}
          label="Total Credit" value={`GHS ${fmtMoney(totals.credit)}`}
          sub={`${totals.creditors} ahead of bill`} />
        <StatTile color="warning" icon={<PendingIcon fontSize="small" />}
          label="Pending Approval" value={`GHS ${fmtMoney(totals.pending)}`}
          sub="Cash queued in collector approvals" />
        <StatTile color="primary" icon={<DebtorIcon fontSize="small" />}
          label="Subscriptions Shown" value={totals.count}
          sub={filtIncome ? filtIncome.type : 'All income types'} />
      </Box>

      {/* Filters */}
      <Card sx={{ borderRadius: 2 }}>
        <Box sx={{
          px: 2.5, py: 1.4, borderBottom: 1, borderColor: 'divider',
          display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper', flexWrap: 'wrap',
        }}>
          <BalanceIcon color="primary" />
          <Typography variant="subtitle2" fontWeight={800}>Balances</Typography>
          <Chip size="small" variant="outlined" label={`${rows.length} row${rows.length === 1 ? '' : 's'}`} sx={{ fontWeight: 700 }} />
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Reload">
            <IconButton size="small" onClick={loadBalances}><RefreshIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Button size="small" variant="outlined" startIcon={<PrintIcon fontSize="small" />}
            onClick={printReport} disabled={!rows.length}>
            Print
          </Button>
          <Button size="small" variant="outlined" startIcon={<WordIcon fontSize="small" />}
            onClick={exportWord} disabled={!rows.length}>
            Word
          </Button>
          <Button size="small" variant="outlined" startIcon={<ExportIcon fontSize="small" />}
            onClick={exportCsv} disabled={!rows.length}>
            CSV
          </Button>
        </Box>

        <Box sx={{
          px: 2.5, py: 1.5, display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr 1.2fr 1fr' },
          columnGap: 1.5, rowGap: 1, alignItems: 'center',
          borderBottom: 1, borderColor: 'divider',
        }}>
          <Autocomplete size="small" options={incomeOptions}
            groupBy={(o) => o.cat}
            value={filtIncome}
            onChange={(_, v) => setFiltIncome(v)}
            getOptionLabel={(o) => o?.type || ''}
            isOptionEqualToValue={(a, b) => a.trans === b.trans}
            renderInput={(p) => (
              <TextField {...p} label="Income type" placeholder="All types"
                InputProps={{ ...p.InputProps,
                  startAdornment: (
                    <>
                      <IncomeIcon fontSize="small" sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }} />
                      {p.InputProps.startAdornment}
                    </>
                  ) }} />
            )} />

          <Autocomplete size="small" options={classOptions}
            value={filtClass || null}
            onChange={(_, v) => setFiltClass(v || '')}
            renderInput={(p) => (
              <TextField {...p} label="Class" placeholder="All classes"
                InputProps={{ ...p.InputProps,
                  startAdornment: (
                    <>
                      <ClassIcon fontSize="small" sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }} />
                      {p.InputProps.startAdornment}
                    </>
                  ) }} />
            )} />

          <TextField size="small" label="Search name / index"
            value={filtQuery} onChange={(e) => setFiltQuery(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />

          <ToggleButtonGroup size="small" exclusive
            value={only}
            onChange={(_, v) => setOnly(v ?? '')}>
            <ToggleButton value=""        sx={{ textTransform: 'none', fontWeight: 700 }}>All</ToggleButton>
            <ToggleButton value="owing"   sx={{ textTransform: 'none', fontWeight: 700, color: 'error.main' }}>Owing</ToggleButton>
            <ToggleButton value="credit"  sx={{ textTransform: 'none', fontWeight: 700, color: 'success.main' }}>Credit</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <CardContent sx={{ pt: 1 }}>
          {loading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
          ) : rows.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
              <BalanceIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography fontWeight={700}>No balances match the filters</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
              <Table size="small" sx={{
                '& .MuiTableCell-head': {
                  bgcolor: 'grey.50', fontSize: 11, fontWeight: 800,
                  textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary',
                },
                '& tbody .MuiTableRow-root:hover': {
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                },
              }}>
                {/* "Pending" and "Last Pending" meant nothing to anyone
                    reading this page. They are cash a collector has
                    already taken from the learner that the bursar has
                    not approved yet — real money, in a drawer, not yet
                    in the books. Named and explained accordingly. */}
                <TableHead>
                  <TableRow>
                    <TableCell>Student</TableCell>
                    <TableCell>Index</TableCell>
                    <TableCell>Class</TableCell>
                    <TableCell>Income Type</TableCell>
                    <TableCell align="right" sx={{ width: 100 }}>
                      <HeadCell label="Daily" hint="What this learner is charged per school day for this income type." />
                    </TableCell>
                    <TableCell align="right" sx={{ width: 110 }}>
                      <HeadCell label="Owes" hint="Unpaid days that have built up. This is what the family still owes." />
                    </TableCell>
                    <TableCell align="right" sx={{ width: 110 }}>
                      <HeadCell label="Paid ahead" hint="Money paid in advance, sitting as credit. Future days come out of this before any cash is asked for." />
                    </TableCell>
                    <TableCell align="right" sx={{ width: 150 }}>
                      <HeadCell label="Awaiting approval"
                        hint="Cash a collector has already taken from this learner but the bursar has not approved yet. It is not in the books until it is approved, so it has not reduced what they owe." />
                    </TableCell>
                    <TableCell sx={{ width: 170 }}>
                      <HeadCell label="Collected on"
                        hint="When that un-approved cash was taken. An old date here means money has been sitting unapproved for a while." />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(r => (
                    <TableRow key={r.id} sx={{ '& td': { fontSize: 12, py: 0.7 } }}>
                      {/* Names were wrapping onto three lines, tripling
                          every row's height. With the page widened there
                          is room to keep each learner on one line. */}
                      <TableCell sx={{
                        fontWeight: 700, whiteSpace: 'nowrap',
                        maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        <Tooltip title={r.student_name || ''} arrow>
                          <span>{r.student_name || '-'}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.student_id}</TableCell>
                      <TableCell>
                        {r.class_name
                          ? <Chip size="small" label={r.class_name} variant="outlined"
                              sx={{ height: 20, fontWeight: 700 }} />
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.6} alignItems="center">
                          <IncomeIcon fontSize="inherit" color="primary" />
                          <Typography fontSize={12} fontWeight={700}>{r.income_type}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={TNUM}>GHS {fmtMoney(r.daily_amount)}</TableCell>
                      <TableCell align="right" sx={{
                        ...TNUM, color: Number(r.arrears) > 0 ? 'error.main' : 'text.secondary',
                        fontWeight: Number(r.arrears) > 0 ? 800 : 500,
                      }}>
                        {fmtMoney(r.arrears)}
                      </TableCell>
                      <TableCell align="right" sx={{
                        ...TNUM, color: Number(r.credit) > 0 ? 'success.main' : 'text.secondary',
                        fontWeight: Number(r.credit) > 0 ? 800 : 500,
                      }}>
                        {fmtMoney(r.credit)}
                      </TableCell>
                      <TableCell align="right" sx={{
                        ...TNUM, color: Number(r.pending_payment) > 0 ? 'warning.main' : 'text.disabled',
                        fontWeight: Number(r.pending_payment) > 0 ? 800 : 500,
                      }}>
                        {Number(r.pending_payment) > 0 ? `GHS ${fmtMoney(r.pending_payment)}` : '-'}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>
                        {fmtDateTime(r.last_pending_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
        </>
        )}
        </Box>
      </Box>
    </Box>
  );
}
